import React, { Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AnimatePresence } from 'framer-motion';
import { AuthProvider, useAuth } from './AuthContext';
import { ThemeProvider } from './ThemeContext';
import { Toaster } from './components/ui/sonner';
import ErrorBoundary from './components/ErrorBoundary';
import { AnimatedPage } from './components/AnimatedPage';
import FounderBanner from './components/founder/FounderBanner';
import PlatformKillswitchBanner from './components/platform/PlatformKillswitchBanner';
import '@/App.css';

// Pages — lazy loaded for performance
const LandingPage = lazy(() => import('./pages/LandingPage'));
const LoginPage = lazy(() => import('./pages/LoginPage'));
const RegisterPage = lazy(() => import('./pages/RegisterPage'));
const SetPasswordPage = lazy(() => import('./pages/SetPasswordPage'));
const Dashboard = lazy(() => import('./pages/Dashboard'));
const MessagesPage = lazy(() => import('./pages/MessagesPage'));
const FamilyTimelinePage = lazy(() => import('./pages/FamilyTimelinePage'));
const RegulatorPage = lazy(() => import('./pages/RegulatorPage'));
const TrainingAudioStatusPage = lazy(() => import('./pages/TrainingAudioStatusPage'));
const LegacyLearnRedirect = lazy(() => import('./pages/LegacyLearnRedirect'));
const ContentQualityPage = lazy(() => import('./pages/ContentQualityPage'));
const TrainingLayout = lazy(() => import('./components/training/TrainingLayout'));
const TrainingEngineList = lazy(() => import('./pages/TrainingEngineList'));
const TrainingEngineModule = lazy(() => import('./pages/TrainingEngineModule'));
const ManagerTrainingStaffPage = lazy(() => import('./pages/ManagerTrainingStaffPage'));
const ManagerComplianceAlertsPage = lazy(() => import('./pages/ManagerComplianceAlertsPage'));
const CompanyDashboardEnhanced = lazy(() => import('./pages/CompanyDashboardEnhanced'));
const EmployerVerificationDashboard = lazy(() => import('./pages/EmployerVerificationDashboard'));
const PricingPage = lazy(() => import('./pages/PricingPage'));
const PaymentSuccess = lazy(() => import('./pages/PaymentSuccess'));
const WellbeingHub = lazy(() => import('./pages/WellbeingHub'));
const AboutPage = lazy(() => import('./pages/AboutPage'));
const SafeguardingPage = lazy(() => import('./pages/SafeguardingPage'));
const AccessibilityPage = lazy(() => import('./pages/AccessibilityPage'));
const ContactPage = lazy(() => import('./pages/ContactPage'));
const MagicLinkVerifyPage = lazy(() => import('./pages/MagicLinkVerifyPage'));
const ResetPasswordPage = lazy(() => import('./pages/ResetPasswordPage'));
const VerifyCertificatePage = lazy(() => import('./pages/VerifyCertificatePage'));
const CertificateVerifyPage = lazy(() => import('./pages/CertificateVerifyPage'));
const CpdRecordPage = lazy(() => import('./pages/CpdRecordPage'));
const RecallTestPage = lazy(() => import('./pages/RecallTestPage'));
const ScannerPage = lazy(() => import('./pages/ScannerPage'));
const SyncHistoryPage = lazy(() => import('./pages/SyncHistoryPage'));
// Legal pages (UK Compliance)
const PrivacyPolicyPage = lazy(() => import('./pages/PrivacyPolicyPage'));
const TermsPage = lazy(() => import('./pages/TermsPage'));
const CookiePolicyPage = lazy(() => import('./pages/CookiePolicyPage'));
const RefundPolicyPage = lazy(() => import('./pages/RefundPolicyPage'));
// Visit Verification Documentation
const ClientConsentFormPage = lazy(() => import('./pages/ClientConsentFormPage'));
const StaffNotificationTemplatePage = lazy(() => import('./pages/StaffNotificationTemplatePage'));
const EmployerGuidancePage = lazy(() => import('./pages/EmployerGuidancePage'));
const SafeguardingNotePolicyPage = lazy(() => import('./pages/SafeguardingNotePolicyPage'));
const VisitVerificationCompliancePage = lazy(() => import('./pages/VisitVerificationCompliancePage'));
// Gamification
const LeaderboardPage = lazy(() => import('./pages/LeaderboardPage'));
// Incidents
const IncidentListPage = lazy(() => import('./pages/IncidentListPage'));
const IncidentDetailPage = lazy(() => import('./pages/IncidentDetailPage'));
const IncidentForm = lazy(() => import('./components/incidents/IncidentForm'));
const ManagerCareDashboard = lazy(() => import('./pages/ManagerCareDashboard'));
const VisitDetailPage = lazy(() => import('./pages/VisitDetailPage'));
const EmarPage = lazy(() => import('./pages/EmarPage'));
const EmarHistoryPage = lazy(() => import('./pages/EmarHistoryPage'));
const CarePlanEditor = lazy(() => import('./pages/CarePlanEditor'));
const ClientCommandCentre = lazy(() => import('./pages/ClientCommandCentre'));
const InvoicesListPage = lazy(() => import('./pages/InvoicesListPage'));
const InvoiceDetailPage = lazy(() => import('./pages/InvoiceDetailPage'));
const DispatchLandingPage = lazy(() => import('./pages/DispatchLandingPage'));
const FounderAdminPage = lazy(() => import('./pages/FounderAdminPage'));
const MfaSetupPage = lazy(() => import('./pages/MfaSetupPage'));
const SchedulePage = lazy(() => import('./pages/SchedulePage'));
const MyRotaPage = lazy(() => import('./pages/MyRotaPage'));
const MyProfilePage = lazy(() => import('./pages/MyProfilePage'));
const StaffMapPage = lazy(() => import('./pages/StaffMapPage'));
const ManagePage = lazy(() => import('./pages/ManagePage'));
const TodayPage = lazy(() => import('./pages/TodayPage'));
const CarerVisitCompletePage = lazy(() => import('./pages/CarerVisitCompletePage'));
const CarerTapVisitPage = lazy(() => import('./pages/CarerTapVisitPage'));
const CQCComplianceDashboard = lazy(() => import('./pages/CQCComplianceDashboard'));
const StaffCompetencyPage = lazy(() => import('./pages/StaffCompetencyPage'));
const StaffDirectoryPage = lazy(() => import('./pages/StaffDirectoryPage'));
const ShadowingPage = lazy(() => import('./pages/ShadowingPage'));
const TrialAnalyticsPage = lazy(() => import('./pages/TrialAnalyticsPage'));
const CompetencyTemplatesPage = lazy(() => import('./pages/CompetencyTemplatesPage'));
const CompetencySessionsPage = lazy(() => import('./pages/CompetencySessionsPage'));
const ManagerReviewPage = lazy(() => import('./pages/ManagerReviewPage'));
const SupervisorReviewPanel = lazy(() => import('./pages/SupervisorReviewPanel'));
const MultiBranchDashboard = lazy(() => import('./pages/MultiBranchDashboard'));
const BranchDetailPage = lazy(() => import('./pages/BranchDetailPage'));
const IntelligenceDashboard = lazy(() => import('./pages/IntelligenceDashboard'));
const PassportPage = lazy(() => import('./pages/PassportPage'));
const MyPassportWallet = lazy(() => import('./pages/MyPassportWallet'));
const PassportAnalytics = lazy(() => import('./pages/PassportAnalytics'));
const PassportTransferPage = lazy(() => import('./pages/PassportTransferPage'));
const RegistryConnectorPage = lazy(() => import('./pages/RegistryConnectorPage'));
const CertificateUploadPage = lazy(() => import('./pages/CertificateUploadPage'));
const CertificateVerificationPage = lazy(() => import('./pages/CertificateVerificationPage'));
const EmployerRequestPassportPage = lazy(() => import('./pages/EmployerRequestPassportPage'));
const PassportRequestsPage = lazy(() => import('./pages/PassportRequestsPage'));
const PublicPassportViewPage = lazy(() => import('./pages/PublicPassportViewPage'));
const PassportBillingPage = lazy(() => import('./pages/PassportBillingPage'));
// Marketing Pages (Phase 6 Website Overhaul)
const FeaturesPage = lazy(() => import('./pages/FeaturesPage'));
const PassportMarketingPage = lazy(() => import('./pages/PassportMarketingPage'));
const WorkforceDevelopmentPage = lazy(() => import('./pages/WorkforceDevelopmentPage'));
const CareModulesMarketingPage = lazy(() => import('./pages/CareModulesMarketingPage'));
const CareOperationsMarketingPage = lazy(() => import('./pages/CareOperationsMarketingPage'));
const ComplianceMarketingPage = lazy(() => import('./pages/ComplianceMarketingPage'));
const SchedulingMarketingPage = lazy(() => import('./pages/SchedulingMarketingPage'));
const IntelligenceMarketingPage = lazy(() => import('./pages/IntelligenceMarketingPage'));
const TransfersMarketingPage = lazy(() => import('./pages/TransfersMarketingPage'));
const EmployerMarketingPage = lazy(() => import('./pages/EmployerMarketingPage'));
const ProvidersMarketingPage = lazy(() => import('./pages/ProvidersMarketingPage'));
const WorkerMarketingPage = lazy(() => import('./pages/WorkerMarketingPage'));
const IntegrationsPage = lazy(() => import('./pages/IntegrationsPage'));
const SecurityPage = lazy(() => import('./pages/SecurityPage'));
const IntegrationHubAdmin = lazy(() => import('./pages/IntegrationHubAdmin'));
const WeightConfigPage = lazy(() => import('./pages/WeightConfigPage'));
const UniversalPassportPage = lazy(() => import('./pages/UniversalPassportPage'));
const PassportShareViewPage = lazy(() => import('./pages/PassportShareViewPage'));
const PassportVerifyPage = lazy(() => import('./pages/PassportVerifyPage'));
// Navigation v1.0 — Placeholder & Redirect Pages
const QualificationsPage = lazy(() => import('./pages/QualificationsPage'));
const WorkHistoryPage = lazy(() => import('./pages/WorkHistoryPage'));
const SkillsInsightsPage = lazy(() => import('./pages/SkillsInsightsPage'));
const MobileAppPage = lazy(() => import('./pages/MobileAppPage'));
const HelpCentrePage = lazy(() => import('./pages/HelpCentrePage'));
const EmployerLearningPage = lazy(() => import('./pages/EmployerLearningPage'));
const EmployerOnboardingPage = lazy(() => import('./pages/EmployerOnboardingPage'));
const CareModuleOnboarding = lazy(() => import('./pages/CareModuleOnboarding'));
const CareCertificatePage = lazy(() => import('./pages/CareCertificatePage'));
const ManagerCareCertificatePage = lazy(() => import('./pages/ManagerCareCertificatePage'));
const ComplianceDashboardPlaceholder = lazy(() => import('./pages/ComplianceDashboardPlaceholder'));
const AnalyticsPlaceholder = lazy(() => import('./pages/AnalyticsPlaceholder'));
const LearningRedirect = lazy(() => import('./pages/LearningRedirect'));

// Components
import WellbeingBar from './components/WellbeingBar';
import CookieConsent from './components/CookieConsent';
import { EmployerRoute } from './components/EmployerRoute';
import { FeatureRoute } from './components/FeatureRoute';

// Protected Route Component
const ProtectedRoute = ({ children, allowPassportOnly = false }) => {
  const { isAuthenticated, loading, user } = useAuth();
  const location = useLocation();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#F7F4ED' }}>
        <div className="text-center">
          <div className="w-14 h-14 border-4 rounded-full animate-spin mx-auto mb-4" style={{ borderColor: '#A8C3A0', borderTopColor: 'transparent' }}></div>
          <p className="text-sm" style={{ color: 'rgba(47, 70, 50, 0.7)' }}>Loading...</p>
        </div>
      </div>
    );
  }
  
  if (!isAuthenticated) {
    const returnUrl = `${location.pathname}${location.search}${location.hash}`;
    return <Navigate to={`/login?returnUrl=${encodeURIComponent(returnUrl)}`} replace />;
  }
  
  // Block passport_only users from standard dashboards
  if (user?.role === 'passport_only' && !allowPassportOnly) {
    return <Navigate to="/passport/start" replace />;
  }
  
  return children;
};

const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#F7F4ED' }}>
    <div className="text-center">
      <div className="w-12 h-12 border-4 rounded-full animate-spin mx-auto mb-3" style={{ borderColor: '#A8C3A0', borderTopColor: 'transparent' }}></div>
      <p className="text-sm" style={{ color: 'rgba(47, 70, 50, 0.7)' }}>Loading...</p>
    </div>
  </div>
);

// Redirect logged-in users away from /register (they should never see register as an upgrade path)
const AuthRedirectGuard = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <PageLoader />;
  if (user) {
    const dest = (user.role === 'company_admin' || user.role === 'company_staff' || user.role === 'supervisor')
      ? '/company/dashboard'
      : '/dashboard';
    return <Navigate to={dest} replace />;
  }
  return children;
};

function App() {
  return (
    <ErrorBoundary>
    <ThemeProvider>
    <AuthProvider>
      <BrowserRouter>
        {/* Skip to content — WCAG 2.4.1 */}
        <a href="#main-content" className="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 focus:z-[9999] focus:bg-soft-teal-500 focus:text-white focus:px-4 focus:py-2 focus:rounded-lg focus:text-sm focus:font-medium">
          Skip to main content
        </a>
        <div className="App" id="main-content" role="main">
          <FounderBanner />
          <PlatformKillswitchBanner />
          <Suspense fallback={<PageLoader />}>
          <AnimatePresence mode="wait">
          <AnimatedPage>
          <Routes>
            {/* Public Routes */}
            <Route path="/" element={<LandingPage />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/register" element={<AuthRedirectGuard><RegisterPage /></AuthRedirectGuard>} />
            <Route path="/set-password" element={<SetPasswordPage />} />
            <Route path="/pricing" element={<PricingPage />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/safeguarding" element={<SafeguardingPage />} />
            <Route path="/accessibility" element={<AccessibilityPage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/auth/magic-link" element={<MagicLinkVerifyPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/verify" element={<VerifyCertificatePage />} />
            <Route path="/verify/:certificateId" element={<VerifyCertificatePage />} />
            <Route path="/verify-certificate/:certificateId" element={<CertificateVerifyPage />} />
            <Route path="/employer/request-passport" element={<EmployerRequestPassportPage />} />
            <Route path="/passport-view/:accessToken" element={<PublicPassportViewPage />} />
            <Route path="/features" element={<FeaturesPage />} />
            <Route path="/passport" element={<PassportMarketingPage />} />
            <Route path="/workforce-development" element={<WorkforceDevelopmentPage />} />
            <Route path="/care-modules" element={<CareModulesMarketingPage />} />
            <Route path="/care-operations" element={<CareOperationsMarketingPage />} />
            <Route path="/compliance" element={<ComplianceMarketingPage />} />
            <Route path="/scheduling" element={<SchedulingMarketingPage />} />
            <Route path="/intelligence" element={<IntelligenceMarketingPage />} />
            <Route path="/transfers" element={<TransfersMarketingPage />} />
            <Route path="/employer" element={<EmployerMarketingPage />} />
            <Route path="/providers" element={<ProvidersMarketingPage />} />
            <Route path="/worker" element={<WorkerMarketingPage />} />
            <Route path="/integrations" element={<IntegrationsPage />} />
            <Route path="/security" element={<SecurityPage />} />
            <Route path="/passport/start" element={<UniversalPassportPage />} />
            <Route path="/passport/create" element={<Navigate to="/passport/start" replace />} />
            <Route path="/onboarding/care-module" element={<ProtectedRoute allowPassportOnly><CareModuleOnboarding /></ProtectedRoute>} />
            <Route path="/care-module" element={<Navigate to="/onboarding/care-module" replace />} />
            <Route path="/care-certificate" element={<ProtectedRoute><CareCertificatePage /></ProtectedRoute>} />
            <Route path="/manager/care-certificate" element={<ProtectedRoute><ManagerCareCertificatePage /></ProtectedRoute>} />
            <Route path="/manager/compliance-alerts" element={<ProtectedRoute><ManagerComplianceAlertsPage /></ProtectedRoute>} />
            <Route path="/passport/share/:token" element={<PassportShareViewPage />} />
            <Route path="/passport/verify/:passportId" element={<PassportVerifyPage />} />
            {/* Navigation v1.0 — Worker pages */}
            <Route path="/qualifications" element={<QualificationsPage />} />
            <Route path="/learning" element={<LearningRedirect />} />
            <Route path="/passport/history" element={<WorkHistoryPage />} />
            <Route path="/insights" element={<SkillsInsightsPage />} />
            <Route path="/app" element={<MobileAppPage />} />
            <Route path="/help" element={<HelpCentrePage />} />
            {/* Navigation v1.0 — Employer pages (role-guarded) */}
            <Route path="/employers/verification" element={<EmployerRequestPassportPage />} />
            <Route path="/employers/dashboard" element={
              <EmployerRoute fallback={<ComplianceDashboardPlaceholder />}>
                <CompanyDashboardEnhanced />
              </EmployerRoute>
            } />
            <Route path="/employers/analytics" element={
              <EmployerRoute fallback={<AnalyticsPlaceholder />}>
                <TrialAnalyticsPage />
              </EmployerRoute>
            } />
            <Route path="/employers/learning" element={
              <EmployerRoute>
                <Dashboard />
              </EmployerRoute>
            } />
            <Route path="/employers/onboarding" element={
              <EmployerRoute fallback={<EmployerOnboardingPage />}>
                <CompanyDashboardEnhanced />
              </EmployerRoute>
            } />
            {/* Trust & Security alias */}
            <Route path="/trust" element={<Navigate to="/security" replace />} />
            
            {/* Protected Routes */}
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <Dashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/messages"
              element={
                <ProtectedRoute>
                  <MessagesPage />
                </ProtectedRoute>
              }
            />
            <Route path="/family" element={<FamilyTimelinePage />} />
            <Route path="/regulator" element={<RegulatorPage />} />
            <Route path="/admin/training/audio-status" element={<TrainingAudioStatusPage />} />
            <Route
              path="/learn/:moduleId"
              element={
                <ProtectedRoute>
                  <LegacyLearnRedirect />
                </ProtectedRoute>
              }
            />
            <Route
              path="/training"
              element={
                <ProtectedRoute>
                  <TrainingLayout>
                    <TrainingEngineList />
                  </TrainingLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/training/cpd-record"
              element={
                <ProtectedRoute>
                  <CpdRecordPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/training/recall/:recallId"
              element={
                <ProtectedRoute>
                  <RecallTestPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/manager/training/content-quality"
              element={
                <ProtectedRoute>
                  <TrainingLayout>
                    <ContentQualityPage />
                  </TrainingLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/training/:moduleId"
              element={
                <ProtectedRoute>
                  <TrainingLayout>
                    <TrainingEngineModule />
                  </TrainingLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/manager/training/staff/:uid"
              element={
                <ProtectedRoute>
                  <TrainingLayout>
                    <ManagerTrainingStaffPage />
                  </TrainingLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/company/dashboard"
              element={
                <ProtectedRoute>
                  <CompanyDashboardEnhanced />
                </ProtectedRoute>
              }
            />
            <Route
              path="/company/verification"
              element={
                <ProtectedRoute>
                  <EmployerVerificationDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/company/integrations"
              element={
                <ProtectedRoute>
                  <IntegrationHubAdmin />
                </ProtectedRoute>
              }
            />
            <Route
              path="/company/weight-config"
              element={
                <ProtectedRoute>
                  <WeightConfigPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/payment-success"
              element={
                <ProtectedRoute>
                  <PaymentSuccess />
                </ProtectedRoute>
              }
            />
            <Route
              path="/incidents"
              element={
                <FeatureRoute feature="incident_reporting">
                  <IncidentListPage />
                </FeatureRoute>
              }
            />
            <Route
              path="/incidents/new"
              element={
                <FeatureRoute feature="incident_reporting">
                  <IncidentForm />
                </FeatureRoute>
              }
            />
            <Route
              path="/incidents/:id"
              element={
                <FeatureRoute feature="incident_reporting">
                  <IncidentDetailPage />
                </FeatureRoute>
              }
            />
            <Route
              path="/care-management"
              element={
                <FeatureRoute feature="care_plans">
                  <ManagerCareDashboard />
                </FeatureRoute>
              }
            />
            <Route
              path="/schedule"
              element={
                <FeatureRoute feature="auto_schedule">
                  <SchedulePage />
                </FeatureRoute>
              }
            />
            <Route
              path="/my-rota"
              element={
                <ProtectedRoute>
                  <MyRotaPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/my-profile"
              element={
                <ProtectedRoute>
                  <MyProfilePage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/staff-map"
              element={
                <ProtectedRoute>
                  <StaffMapPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/manage"
              element={
                <ProtectedRoute>
                  <ManagePage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/today"
              element={
                <ProtectedRoute>
                  <TodayPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/carer/visit/:visitId/complete"
              element={
                <ProtectedRoute>
                  <CarerVisitCompletePage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/carer/visit/:visitId/tap"
              element={
                <ProtectedRoute>
                  <CarerTapVisitPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/company/compliance"
              element={
                <FeatureRoute feature="safeguarding">
                  <CQCComplianceDashboard />
                </FeatureRoute>
              }
            />
            <Route
              path="/staff-competency"
              element={
                <FeatureRoute feature="care_training">
                  <StaffCompetencyPage />
                </FeatureRoute>
              }
            />
            <Route
              path="/staff-directory"
              element={
                <ProtectedRoute>
                  <StaffDirectoryPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/shadowing"
              element={
                <FeatureRoute feature="care_training">
                  <ShadowingPage />
                </FeatureRoute>
              }
            />
            <Route
              path="/trial-analytics"
              element={
                <ProtectedRoute>
                  <TrialAnalyticsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/competency-templates"
              element={
                <FeatureRoute feature="care_training">
                  <CompetencyTemplatesPage />
                </FeatureRoute>
              }
            />
            <Route
              path="/competency-sessions"
              element={
                <FeatureRoute feature="care_training">
                  <CompetencySessionsPage />
                </FeatureRoute>
              }
            />
            <Route
              path="/manager-review"
              element={
                <ProtectedRoute>
                  <ManagerReviewPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/session-review/:sessionId"
              element={
                <ProtectedRoute>
                  <SupervisorReviewPanel />
                </ProtectedRoute>
              }
            />
            <Route
              path="/multi-branch"
              element={
                <FeatureRoute feature="multi_branch">
                  <MultiBranchDashboard />
                </FeatureRoute>
              }
            />
            <Route
              path="/multi-branch/:branchId"
              element={
                <FeatureRoute feature="multi_branch">
                  <BranchDetailPage />
                </FeatureRoute>
              }
            />
            <Route
              path="/company/intelligence"
              element={
                <FeatureRoute feature="workforce_intelligence">
                  <IntelligenceDashboard />
                </FeatureRoute>
              }
            />
            <Route
              path="/passport/:staffId"
              element={
                <ProtectedRoute>
                  <PassportPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/my-passport"
              element={
                <ProtectedRoute>
                  <MyPassportWallet />
                </ProtectedRoute>
              }
            />
            <Route
              path="/passport-analytics"
              element={
                <ProtectedRoute>
                  <PassportAnalytics />
                </ProtectedRoute>
              }
            />
            <Route
              path="/passport-transfers"
              element={
                <ProtectedRoute>
                  <PassportTransferPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/registry-connectors"
              element={
                <ProtectedRoute>
                  <RegistryConnectorPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/passport/upload"
              element={
                <ProtectedRoute>
                  <CertificateUploadPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/certificates/verify"
              element={
                <ProtectedRoute>
                  <CertificateVerificationPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/passport-requests"
              element={
                <ProtectedRoute>
                  <PassportRequestsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/passport-billing"
              element={
                <ProtectedRoute>
                  <PassportBillingPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/wellbeing"
              element={
                <ProtectedRoute>
                  <WellbeingHub />
                </ProtectedRoute>
              }
            />
            <Route
              path="/leaderboard"
              element={
                <ProtectedRoute>
                  <LeaderboardPage />
                </ProtectedRoute>
              }
            />
            {/* Scanner page handles its own auth - shows friendly sign-in screen for QR scans */}
            <Route path="/scan" element={<ScannerPage />} />
            <Route path="/sync-history" element={<ProtectedRoute><SyncHistoryPage /></ProtectedRoute>} />
            <Route
              path="/visits/:visitId"
              element={
                <ProtectedRoute>
                  <VisitDetailPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/emar"
              element={
                <FeatureRoute feature="emar">
                  <EmarPage />
                </FeatureRoute>
              }
            />
            <Route
              path="/emar/history"
              element={
                <FeatureRoute feature="emar">
                  <EmarHistoryPage />
                </FeatureRoute>
              }
            />
            <Route
              path="/care-plan/:clientId"
              element={
                <FeatureRoute feature="care_plans">
                  <ClientCommandCentre />
                </FeatureRoute>
              }
            />
            <Route
              path="/care-plan/:clientId/editor"
              element={
                <FeatureRoute feature="care_plans">
                  <CarePlanEditor />
                </FeatureRoute>
              }
            />
            <Route
              path="/invoices"
              element={
                <ProtectedRoute>
                  <InvoicesListPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/invoices/:invoiceId"
              element={
                <ProtectedRoute>
                  <InvoiceDetailPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dispatches/:dispatchId"
              element={
                <ProtectedRoute>
                  <DispatchLandingPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/founder"
              element={
                <ProtectedRoute>
                  <FounderAdminPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/mfa-setup"
              element={
                <ProtectedRoute>
                  <MfaSetupPage />
                </ProtectedRoute>
              }
            />

            {/* Legal Pages (UK Compliance) */}
            <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
            <Route path="/terms" element={<TermsPage />} />
            <Route path="/cookie-policy" element={<CookiePolicyPage />} />
            <Route path="/refund-policy" element={<RefundPolicyPage />} />

            {/* Visit Verification Documentation */}
            <Route path="/client-consent-form" element={<ClientConsentFormPage />} />
            <Route path="/staff-notification-template" element={<StaffNotificationTemplatePage />} />
            <Route path="/employer-guidance" element={<EmployerGuidancePage />} />
            <Route path="/safeguarding-note-policy" element={<SafeguardingNotePolicyPage />} />
            <Route path="/visit-verification-compliance" element={<VisitVerificationCompliancePage />} />

            {/* Fallback */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          </AnimatedPage>
          </AnimatePresence>
          </Suspense>
          <WellbeingBar />
          <CookieConsent />
          <Toaster position="top-right" />
        </div>
      </BrowserRouter>
    </AuthProvider>
    </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;